// Stage-4 contact-verification step, shown inside the login card when a sign-in
// succeeds at Logto but the account still needs its email and/or phone verified.
// It walks the outstanding channels one at a time: email first (a code goes to
// the inbox), then phone (adding a number first if none is on file). When the
// backend reports both verified it issues the session and we continue into the app.

import { useEffect, useState } from 'react';
import { Mail, Phone, ShieldCheck, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth, type VerifyStatus, type VerifySubmitResult } from '@/auth/AuthContext';

type Channel = 'email' | 'phone';

interface Props {
  verifyToken: string;
  initialStatus?: VerifyStatus;
  onComplete: () => void;
}

export function VerificationStep({ verifyToken, initialStatus, onComplete }: Props) {
  const { t } = useTranslation(['auth', 'common']);
  const { verifyAddPhone, verifySendCode, verifySubmitCode } = useAuth();
  const [status, setStatus] = useState<VerifyStatus | undefined>(initialStatus);
  const [channel, setChannel] = useState<Channel>('email');
  const [phase, setPhase] = useState<'phone-entry' | 'code' | 'sending' | 'busy'>('code');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  // Decide the active channel + phase from the current status.
  useEffect(() => {
    if (!status) { setChannel('email'); setPhase('code'); return; }
    if (!status.emailVerified) {
      setChannel('email');
      setPhase('code');
      if (!sentTo) void doSend('email');
      return;
    }
    // Email done → move to phone.
    setChannel('phone');
    if (!status.hasPhone) { setPhase('phone-entry'); return; }
    if (!status.phoneVerified) { setPhase('code'); if (sentTo !== 'phone') void doSend('phone'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function doSend(ch: Channel) {
    setError(null);
    setPhase('sending');
    try {
      await verifySendCode(verifyToken, ch);
      setSentTo(ch);
      setCode('');
      setPhase('code');
    } catch (e: unknown) {
      const err = (e as { response?: { data?: { error?: string }; status?: number } })?.response;
      setError(
        err?.data?.error === 'not_configured' ? t('verify.errSmsUnavailable', 'SMS verification is unavailable right now. Contact your administrator.')
        : err?.data?.error === 'cooldown' ? t('verify.errCooldown', 'Please wait a moment before requesting another code.')
        : t('verify.errSendFailed', "Couldn't send the code. Please try again."),
      );
      setPhase('code');
    }
  }

  async function onAddPhone() {
    setError(null);
    if (!/^\+?[1-9]\d{6,15}$/.test(phone.trim())) { setError(t('verify.errBadPhone', 'Enter a valid phone number in international format, e.g. +972501234567.')); return; }
    setPhase('busy');
    try {
      await verifyAddPhone(verifyToken, phone.trim());
      await doSend('phone');
    } catch {
      setError(t('verify.errPhoneSave', "Couldn't save that phone number. Please try again."));
      setPhase('phone-entry');
    }
  }

  async function onSubmitCode() {
    setError(null);
    setPhase('busy');
    try {
      const res: VerifySubmitResult = await verifySubmitCode(verifyToken, channel, code.trim());
      if (res.complete) { onComplete(); return; }
      // Not fully done → apply the backend's authoritative per-channel flags and
      // let the effect advance to the outstanding channel.
      setSentTo(null);
      setStatus((prev) => prev ? {
        ...prev,
        emailVerified: res.verified?.email ?? prev.emailVerified,
        phoneVerified: res.verified?.phone ?? prev.phoneVerified,
        hasPhone: res.hasPhone ?? prev.hasPhone,
      } : prev);
    } catch (e: unknown) {
      const errCode = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(
        errCode === 'expired' ? t('verify.errExpired', 'That code expired. We can send a new one.')
        : errCode === 'too_many_attempts' ? t('verify.errTooMany', 'Too many attempts. Request a new code.')
        : t('verify.errBadCode', 'That code is incorrect.'),
      );
      setPhase('code');
    }
  }

  const emailDone = !!status?.emailVerified;
  const phoneDone = !!status?.phoneVerified;

  return (
    <div className="mt-7 space-y-5">
      <div className="flex items-center gap-2 text-fg">
        <ShieldCheck className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">{t('verify.title', 'Verify your account')}</h2>
      </div>
      <p className="text-sm text-muted">
        {t('verify.subtitle', 'Confirm your contact details to finish signing in.')}
      </p>

      {/* Progress: email + phone */}
      <div className="flex gap-2 text-xs">
        <span className={`flex items-center gap-1 rounded-full border px-3 py-1 ${emailDone ? 'border-ok/40 bg-ok/10 text-ok' : channel === 'email' ? 'border-primary bg-primary/15 text-primary' : 'border-border text-muted'}`}>
          <Mail className="h-3.5 w-3.5" /> {t('verify.email', 'Email')} {emailDone ? '✓' : ''}
        </span>
        <span className={`flex items-center gap-1 rounded-full border px-3 py-1 ${phoneDone ? 'border-ok/40 bg-ok/10 text-ok' : channel === 'phone' ? 'border-primary bg-primary/15 text-primary' : 'border-border text-muted'}`}>
          <Phone className="h-3.5 w-3.5" /> {t('verify.phone', 'Phone')} {phoneDone ? '✓' : ''}
        </span>
      </div>

      {phase === 'phone-entry' ? (
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-fg">{t('verify.addPhone', 'Add your mobile number')}</span>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+972 50 123 4567"
              className="w-full rounded-lg border border-border bg-black/20 py-2.5 px-3 text-fg outline-none focus:border-primary"
            />
            <span className="mt-1 block text-xs text-muted">{t('verify.phoneHint', "We'll text you a 6-digit code.")}</span>
          </label>
          {error && <p className="flex items-center gap-2 text-sm text-red-400" role="alert"><AlertTriangle className="h-4 w-4 shrink-0" />{error}</p>}
          <button type="button" onClick={onAddPhone} className="tk-btn-primary w-full py-2.5 text-base">
            {t('verify.sendSms', 'Send code by SMS')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted">
            {channel === 'email'
              ? t('verify.enterEmailCode', { defaultValue: 'Enter the 6-digit code we sent to {{email}}.', email: status?.email ?? t('verify.yourEmail', 'your email') })
              : t('verify.enterPhoneCode', { defaultValue: 'Enter the 6-digit code we texted to {{phone}}.', phone: status?.phoneMasked ?? t('verify.yourPhone', 'your phone') })}
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={8}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="••••••"
            className="tk-num w-full rounded-lg border border-border bg-black/20 py-2.5 px-3 text-center text-xl tracking-[0.5em] text-fg outline-none focus:border-primary"
          />
          {error && <p className="flex items-center gap-2 text-sm text-red-400" role="alert"><AlertTriangle className="h-4 w-4 shrink-0" />{error}</p>}
          <button
            type="button"
            onClick={onSubmitCode}
            disabled={phase === 'busy' || phase === 'sending' || code.length < 4}
            className="tk-btn-primary w-full py-2.5 text-base disabled:opacity-60"
          >
            {phase === 'busy' ? t('verify.verifying', 'Verifying…') : t('verify.verify', 'Verify')}
          </button>
          <button
            type="button"
            onClick={() => doSend(channel)}
            disabled={phase === 'sending'}
            className="w-full text-center text-xs text-muted hover:text-fg"
          >
            {phase === 'sending' ? t('verify.sending', 'Sending…') : t('verify.resend', 'Resend code')}
          </button>
        </div>
      )}
    </div>
  );
}
